<script setup lang="ts">
import type { IArticle } from "~/types";
import { scrollToItem } from "~/helpers";
import { SearchDataModel } from "~/types/models/SearchData.model";
import { ArticleSortOptionEnum } from "~/enums/ArticleSortOptionEnum";
import { WindowSizesEnum } from "~/enums";
import type { ISelectDropdownItem } from "~/components/SelectDropdown/ISelectDropdown";
import { isoToDateConverter } from "~/helpers";
import {
  CardArticle,
  Spinner,
  Pagination,
  type IPaginationProps
} from "@frontiers-vue3/public-pages";
import { useProxyService } from "~/composables/useProxyService";
import { Alert } from "@frontiers-vue3/public-pages";

const LazySelectDropdown = defineAsyncComponent(
  () => import("@/components/SelectDropdown/SelectDropdown.vue")
);

//* Variables
const mainStore = useMain();
const terminology = useTerminologyStore();
const { isImpactMetricsVisible } = useEnvironmentConfigStore().getConfig();
const itemsPerPage = useEnvironmentConfigStore().getConfig().articlesPerPage;
const articlesTerm = terminology.getTerm("articles");
const researchTopicTerm = terminology.getTerm("researchTopic");

const windowSize: Ref<WindowSizesEnum> = useGetWindowSize();
const isDataFetchPending = ref<boolean>(false);
const currentPage = ref<number>(1);
const activeQuickFilter = ref<keyof typeof ArticleSortOptionEnum>("DATE");
const selectedSortOption = ref<ArticleSortOptionEnum>(
  ArticleSortOptionEnum[activeQuickFilter.value]
);
const isImpactViewsVisible = ref<boolean>(isImpactMetricsVisible);

const articlesFetchError = useState<string | undefined>(
  "articlesFetchError",
  () => undefined
);

const chipButtons = [
  { label: "Views", value: "VIEWS", modelValue: false },
  { label: "Type", value: "ARTICLETYPE", modelValue: false },
  { label: "Date", value: "DATE", modelValue: false }
];

const metaData = usePageMetaInfo().getResearchTopicDetailsPageMetaInfo(
  mainStore.currentResearchTopic,
  "/articles"
);

useHead(() => metaData);

const itemsCount = computed((): number => {
  return mainStore.totalArticles;
});

const displayAlertMessage = computed((): boolean => {
  return (
    !isDataFetchPending.value &&
    !computedCurrentArticles.value?.length &&
    !articlesFetchError.value
  );
});

//* Methods
const mapChipButtonsToSelectDropdown = (): ISelectDropdownItem[] => {
  return chipButtons.map((cb) => {
    return { label: cb.label, value: cb.value };
  });
};

const hasArticles = (
  sortOption: number,
  pageNumber: number
): boolean | undefined => {
  if (currentArticles.value.has(sortOption)) {
    return currentArticles.value.get(sortOption)?.has(pageNumber);
  } else return currentArticles.value.has(sortOption);
};

const paginate = async (
  pageNumber: number,
  scroll = false,
  sortby = ArticleSortOptionEnum.DATE
): Promise<void> => {
  isDataFetchPending.value = true;
  const skip = (pageNumber - 1) * itemsPerPage;
  const availablePage = hasArticles(sortby, pageNumber);
  if (!availablePage) {
    const { getArticlesByResearchTopicId } = useRtApiService();

    const articles = await getArticlesByResearchTopicId(
      mainStore.currentResearchTopic.id,
      new SearchDataModel(skip, itemsPerPage, sortby)
    );
    currentArticles.value.set(
      sortby,
      currentArticles.value.get(sortby)!.set(pageNumber, articles || [])
    );
  }

  currentPage.value = pageNumber;
  selectedSortOption.value = sortby;
  if (scroll) scrollToItem("MainTabs");
  isDataFetchPending.value = false;
};

const onFilterSelected = (chipClickedLvalue: string): void => {
  activeQuickFilter.value =
    chipClickedLvalue as keyof typeof ArticleSortOptionEnum;
  paginate(1, false, ArticleSortOptionEnum[activeQuickFilter.value]);
};

const useDropdownMode = computed((): boolean => {
  return ![
    WindowSizesEnum.DESKTOP,
    WindowSizesEnum.LAPTOP,
    WindowSizesEnum.LAPTOP_SM
  ].includes(windowSize.value);
});

const paginationProps = computed(
  (): IPaginationProps => ({
    itemsPerPage: itemsPerPage,
    totalItems: itemsCount.value,
    label: articlesTerm.toLocaleLowerCase(),
    pageNumber: currentPage.value,
    showFirstLastButtons: false
  })
);

const onPageChange = (pageNumber: number, scroll: boolean = true): void => {
  paginate(pageNumber, scroll, ArticleSortOptionEnum[activeQuickFilter.value]);
};

const { data } = useAsyncData("articles", async () => {
  isDataFetchPending.value = true;
  const { getArticles } = useProxyService();
  const articlesData = await getArticles(
    mainStore.currentResearchTopic.id,
    new SearchDataModel(0, itemsPerPage, ArticleSortOptionEnum.DATE),
    { getFromCache: true }
  );

  if (articlesData?.error) {
    console.error("Error fetching articles data. Error:", articlesData.error);
    articlesFetchError.value = articlesData.error;
  }

  isDataFetchPending.value = false;
  return articlesData;
});

const currentArticles = useState<Map<number, Map<number, IArticle[]>>>(
  "articles",
  () =>
    new Map()
      .set(
        ArticleSortOptionEnum.DATE,
        new Map().set(currentPage.value, data.value?.articles)
      )
      .set(ArticleSortOptionEnum.ARTICLETYPE, new Map())
      .set(ArticleSortOptionEnum.VIEWS, new Map())
);

const computedCurrentArticles = computed(() => {
  return (
    currentArticles.value
      .get(selectedSortOption.value)
      ?.get(currentPage.value) || data.value?.articles
  );
});
</script>

<template>
  <section>
    <div class="ArticlesHeader">
      <h2 class="Section__title">
        <span v-if="itemsCount > 0">{{ itemsCount }}</span>
        {{ terminology.getTerm("articles") }}
      </h2>
      <ClientOnly>
        <div v-if="itemsCount && !articlesFetchError">
          <QuickChipFilters
            v-if="!useDropdownMode"
            title="Sort by:"
            :chip-buttons="chipButtons"
            :active-chip="activeQuickFilter"
            @chip-clicked="onFilterSelected" />

          <LazySelectDropdown
            v-if="useDropdownMode"
            :active-item="activeQuickFilter"
            :items="mapChipButtonsToSelectDropdown()"
            @item-selected="onFilterSelected" />
        </div>
      </ClientOnly>
    </div>
    <div
      v-if="!!articlesFetchError && !isDataFetchPending"
      class="Articles Articles--error">
      <Alert type="error" message="Unable to retrieve component data." />
    </div>
    <div v-if="computedCurrentArticles?.length" id="Articles" class="Articles">
      <div class="Articles__CardList">
        <CardArticle
          v-for="article in computedCurrentArticles"
          :id="article.id.toString()"
          :key="article.id"
          :doi="article.doi"
          :type="article.articleType?.toUpperCase() || ''"
          :published-date="
            article.publishedOn ? isoToDateConverter(article.publishedOn) : ''
          "
          :accepted-date="isoToDateConverter(article.acceptedDate)"
          :title="article.title"
          :authors="article.authors ?? []"
          :total-views="isImpactViewsVisible ? article.impactV5?.views || 0 : 0"
          :total-citations="article.impactV5?.citations || 0"
          :image-url="article.imageUrl"
          journal-name=""
          :journal-slug="article.journal.journalSlug"
          :use-new-url="true" />
      </div>
      <Spinner
        v-if="isDataFetchPending && itemsCount"
        class="Spinner"
        size="2xl"
        display="inline-flex" />
      <pagination
        v-if="itemsCount"
        v-bind="paginationProps"
        @new-page="onPageChange" />
    </div>

    <Spinner
      v-if="isDataFetchPending && !computedCurrentArticles?.length"
      class="Spinner"
      size="2xl"
      display="flex" />

    <AlertMessage
      v-if="displayAlertMessage"
      :message="`The ${researchTopicTerm} has been launched recently. ${articlesTerm} will be
        published on this page as soon as they become available.`" />
  </section>
</template>

<style lang="scss">
.Articles {
  display: flex;
  flex-direction: column;
  @include tab-content-width;

  &__CardList {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 32px;
    grid-row-gap: 32px;
    margin-bottom: 56px;

    @media #{$break-laptop} {
      grid-template-columns: repeat(2, 1fr);
    }

    @media #{$break-laptop-sm} {
      max-width: var(--layout-col-14);
    }

    @media #{$break-md} {
      max-width: none;
      -moz-column-gap: 16px;
      grid-column-gap: 16px;
      column-gap: 16px;
      grid-row-gap: 24px;
      row-gap: 24px;
    }

    @media #{$break-xs} {
      grid-template-columns: repeat(1, 1fr);
    }
  }

  .Spinner {
    align-self: center;
    margin: 32px 0;
  }
}

.ArticlesHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  @include tab-content-width;

  &__Spinner {
    flex: 0.5;
  }
}
</style>
